/**
 * ProviderModelStatusTracker — centralized, shared status tracking for every
 * provider/model combination used across the agent, subagents, fallback
 * chains, and the one-shot LLM helper.
 *
 * ## Design
 *
 * - **Single shared instance**: created once per process, injected into every
 *   component that calls providers or resolves fallback chains. The same
 *   in-memory state is visible to the fallback extension, the one-shot
 *   orchestrator, the dispatcher, and fleet-spawn — so a provider blocked
 *   by a rate-limit spike is filtered EVERYWHERE, not just in one path.
 *
 * - **State machine** per (providerId, model) pair:
 *   ```
 *   healthy ──(failure)──▶ degraded ──(more failures)──▶ blocked
 *      ▲                                                    │
 *      └──────────────(success streak / timeout)────────────┘
 *   ```
 *
 * - **Thresholds** are configurable via constructor opts. Defaults:
 *   | Metric | Threshold | Action |
 *   |--------|-----------|--------|
 *   | consecutive failures ≥ 2 | → `degraded` for 30 s |
 *   | rate-limit hits ≥ 3 | → `blocked` for 5 min |
 *   | consecutive failures ≥ 5 | → `blocked` for 5 min |
 *   | success streak ≥ 3 | → back to `healthy` |
 *   | `blocked` timeout elapses | → back to `healthy` |
 *
 * - **Error history**: keeps the last N errors per pair, with session id,
 *   agent id, kind, and message so the WebUI and `/provider-status` can
 *   render a meaningful timeline.
 *
 * - **Events**: emits `provider.status_changed` on every state transition.
 *
 * - **Thread-safety**: not needed — Node.js event-loop concurrency means
 *   all access is single-threaded (async gaps don't race on the Map).
 *
 * @module coordination/provider-status-tracker
 */

import type { EventBus } from '../kernel/events.js';
import type { ProviderErrorKind } from '../types/provider.js';

// ── Public types ────────────────────────────────────────────────────────────

export type ProviderModelState = 'healthy' | 'degraded' | 'blocked';

export interface ErrorHistoryEntry {
  readonly timestamp: number;
  readonly kind: ProviderErrorKind;
  readonly status: number;
  readonly message: string;
  readonly sessionId?: string | undefined;
  readonly agentId?: string | undefined;
  readonly retryAfterMs?: number | undefined;
}

export interface ProviderModelStatus {
  readonly providerId: string;
  readonly model: string;

  /** Current state in the state machine. */
  readonly state: ProviderModelState;

  // ── Failure counters ──
  readonly consecutiveFailures: number;
  readonly totalFailures: number;
  readonly rateLimitHits: number;
  readonly overloadedHits: number;
  readonly serverErrors: number;
  readonly otherErrors: number;

  // ── Success counters ──
  readonly consecutiveSuccesses: number;
  readonly totalSuccesses: number;
  readonly lastSuccessAt: number | null;

  // ── Timing ──
  readonly firstFailureAt: number | null;
  readonly lastFailureAt: number | null;
  /** When the current degraded or blocked state expires (ms epoch), or null. */
  readonly stateExpiresAt: number | null;

  // ── Last-error detail (for WebUI / `/provider-status`) ──
  readonly lastErrorKind: ProviderErrorKind | null;
  readonly lastErrorMessage: string | null;
  readonly lastErrorStatus: number | null;
  readonly lastSessionId: string | null;
  readonly lastAgentId: string | null;

  /** Recent error history (newest-first, capped to `maxErrorHistory`). */
  readonly recentErrors: readonly ErrorHistoryEntry[];
}

export interface ProviderStatusTrackerConfig {
  /**
   * Consecutive failures before entering `degraded`. Default: 2.
   */
  degradedAfterFailures?: number;
  /**
   * Duration (ms) the provider stays in `degraded` before reverting to healthy.
   * Default: 30_000 (30 s).
   */
  degradedDurationMs?: number;
  /**
   * Rate-limit hits (error kind = 'rate_limit') before entering `blocked`.
   * Default: 3.
   */
  blockAfterRateLimitHits?: number;
  /**
   * Consecutive failures before entering `blocked` directly.
   * Default: 5.
   */
  blockAfterFailures?: number;
  /**
   * Duration (ms) the provider stays `blocked`. Default: 300_000 (5 min).
   */
  blockDurationMs?: number;
  /**
   * Cooldown for an explicit exhausted-credit/quota response when the
   * provider does not publish a reset hint. These failures enter the waiting
   * room immediately instead of consuming the ordinary rate-limit threshold.
   * Default: 900_000 (15 min).
   */
  quotaBlockDurationMs?: number;
  /**
   * Consecutive successes needed to leave `degraded` or `blocked` and return
   * to `healthy` (if the timeout hasn't already cleared it). Default: 3.
   */
  recoverAfterSuccesses?: number;
  /**
   * Maximum error history entries kept per (providerId, model) pair.
   * Default: 50.
   */
  maxErrorHistory?: number;
}

// ── Defaults ────────────────────────────────────────────────────────────────

const DEFAULTS = {
  degradedAfterFailures: 2,
  degradedDurationMs: 30_000,
  blockAfterRateLimitHits: 3,
  blockAfterFailures: 5,
  blockDurationMs: 300_000,
  quotaBlockDurationMs: 900_000,
  recoverAfterSuccesses: 3,
  maxErrorHistory: 50,
} satisfies Required<ProviderStatusTrackerConfig>;

// ── Internal mutable state ──────────────────────────────────────────────────

interface MutableProviderModelStatus {
  state: ProviderModelState;
  consecutiveFailures: number;
  totalFailures: number;
  rateLimitHits: number;
  overloadedHits: number;
  serverErrors: number;
  otherErrors: number;
  consecutiveSuccesses: number;
  totalSuccesses: number;
  lastSuccessAt: number | null;
  firstFailureAt: number | null;
  lastFailureAt: number | null;
  stateExpiresAt: number | null;
  lastErrorKind: ProviderErrorKind | null;
  lastErrorMessage: string | null;
  lastErrorStatus: number | null;
  lastSessionId: string | null;
  lastAgentId: string | null;
  recentErrors: ErrorHistoryEntry[];
}

// ── Tracker ─────────────────────────────────────────────────────────────────

export class ProviderModelStatusTracker {
  private readonly cfg: Required<ProviderStatusTrackerConfig>;
  private readonly map = new Map<string, MutableProviderModelStatus>();
  private readonly events: EventBus | undefined;

  constructor(opts?: {
    config?: ProviderStatusTrackerConfig | undefined;
    events?: EventBus | undefined;
  }) {
    this.cfg = { ...DEFAULTS, ...opts?.config };
    this.events = opts?.events;
  }

  // ── Public API ──────────────────────────────────────────────────────────

  /** Resolve the logical health/display identity without changing wire routing. */
  logicalIdentity(providerId: string, model: string): { providerId: string; model: string } {
    return statusIdentity(providerId, model);
  }

  /**
   * Record a successful provider call. Resets consecutive failure counters
   * and may transition out of degraded/blocked.
   */
  recordSuccess(
    providerId: string,
    model: string,
    _meta?: { sessionId?: string | undefined; agentId?: string | undefined },
  ): void {
    ({ providerId, model } = statusIdentity(providerId, model));
    const key = pairKey(providerId, model);
    const s = this.getOrCreate(key, providerId, model);

    s.consecutiveFailures = 0;
    s.consecutiveSuccesses += 1;
    s.totalSuccesses += 1;
    s.lastSuccessAt = Date.now();

    // Success streak recovery: if we've had enough consecutive successes,
    // move back to healthy.
    if (s.state !== 'healthy' && s.consecutiveSuccesses >= this.cfg.recoverAfterSuccesses) {
      const oldState = s.state;
      s.state = 'healthy';
      s.stateExpiresAt = null;
      this.emitStatusChanged(providerId, model, oldState, 'healthy', 'success_streak_recovery');
    }
  }

  /**
   * Record a failed provider call. Transitions state based on thresholds.
   *
   * @returns The new state after recording this failure.
   */
  recordFailure(
    providerId: string,
    model: string,
    kind: ProviderErrorKind,
    status: number,
    message: string,
    meta?: {
      sessionId?: string | undefined;
      agentId?: string | undefined;
      retryAfterMs?: number | undefined;
    },
  ): ProviderModelState {
    ({ providerId, model } = statusIdentity(providerId, model));
    const key = pairKey(providerId, model);
    const s = this.getOrCreate(key, providerId, model);
    const now = Date.now();
    const previousExpiry = s.stateExpiresAt;

    s.consecutiveFailures += 1;
    s.totalFailures += 1;
    s.consecutiveSuccesses = 0;
    s.lastFailureAt = now;
    s.lastErrorKind = kind;
    s.lastErrorMessage = message;
    s.lastErrorStatus = status;
    if (meta?.sessionId) s.lastSessionId = meta.sessionId;
    if (meta?.agentId) s.lastAgentId = meta.agentId;
    if (s.firstFailureAt === null) s.firstFailureAt = now;

    // Per-kind counters
    switch (kind) {
      case 'rate_limit':
      case 'quota_exhausted':
        s.rateLimitHits += 1;
        break;
      case 'overloaded':
        s.overloadedHits += 1;
        break;
      case 'server':
      case 'stream_hang':
        s.serverErrors += 1;
        break;
      default:
        s.otherErrors += 1;
    }

    // Push error history (newest first, capped)
    const entry: ErrorHistoryEntry = Object.freeze({
      timestamp: now,
      kind,
      status,
      message,
      sessionId: meta?.sessionId,
      agentId: meta?.agentId,
      retryAfterMs: meta?.retryAfterMs,
    });
    s.recentErrors.unshift(entry);
    if (s.recentErrors.length > this.cfg.maxErrorHistory) {
      s.recentErrors = s.recentErrors.slice(0, this.cfg.maxErrorHistory);
    }

    // ── State machine transitions ──

    let newState: ProviderModelState = s.state;
    let reason = '';

    // A depleted account/plan is not a transient burst-rate signal. Waiting
    // for two more doomed requests wastes time and can fan the same failure
    // out through subagents, so quarantine this model on the first response.
    const quotaExhausted = kind === 'quota_exhausted' || isQuotaExhausted(kind, status, message);

    // API endpoint unreachable (502 with connection-refused / upstream-down
    // message) is equally non-transient: the provider's upstream is offline
    // and retrying will fail until it comes back. Block immediately instead
    // of exhausting the failure-threshold chain.
    const endpointUnreachable = isEndpointUnreachable(kind, status, message);

    if (quotaExhausted) {
      newState = 'blocked';
      reason = 'quota_exhausted';
      s.stateExpiresAt = now + this.cfg.quotaBlockDurationMs;
    } else if (endpointUnreachable) {
      newState = 'blocked';
      reason = 'endpoint_unreachable';
      s.stateExpiresAt = now + this.cfg.quotaBlockDurationMs;
    }

    if (!quotaExhausted && !endpointUnreachable && s.state === 'healthy') {
      // healthy → degraded (consecutive failures >= threshold)
      if (s.consecutiveFailures >= this.cfg.degradedAfterFailures) {
        newState = 'degraded';
        reason = `consecutive_failures_${s.consecutiveFailures}`;
        s.stateExpiresAt = now + this.cfg.degradedDurationMs;
      }
    }

    if (!quotaExhausted && !endpointUnreachable && (s.state === 'degraded' || s.state === 'healthy')) {
      // → blocked (rate-limit threshold or consecutive failures threshold)
      if (s.rateLimitHits >= this.cfg.blockAfterRateLimitHits) {
        newState = 'blocked';
        reason = `rate_limit_threshold_${this.cfg.blockAfterRateLimitHits}`;
        s.stateExpiresAt = now + this.cfg.blockDurationMs;
      } else if (s.consecutiveFailures >= this.cfg.blockAfterFailures) {
        newState = 'blocked';
        reason = `consecutive_failures_${s.consecutiveFailures}`;
        s.stateExpiresAt = now + this.cfg.blockDurationMs;
      }
    }

    // If the provider sent a Retry-After hint, extend the blocked time
    if (newState !== 'healthy' && meta?.retryAfterMs && meta.retryAfterMs > 0) {
      const hintExpiry = now + meta.retryAfterMs;
      if (s.stateExpiresAt === null || hintExpiry > s.stateExpiresAt) {
        s.stateExpiresAt = hintExpiry;
      }
    }

    if (s.state !== newState) {
      const oldState = s.state;
      s.state = newState;
      this.emitStatusChanged(providerId, model, oldState, newState, reason);
    } else if (s.state !== 'healthy' && s.stateExpiresAt !== previousExpiry) {
      this.emitStatusChanged(providerId, model, s.state, s.state, 'cooldown_extended');
    }

    return newState;
  }

  /**
   * Check if a (providerId, model) pair is currently usable.
   * Returns `true` when healthy or degraded (degraded is still usable,
   * just known flaky). Returns `false` when blocked or when the error
   * kind is `auth` (no point retrying ever).
   */
  isAvailable(providerId: string, model: string): boolean {
    ({ providerId, model } = statusIdentity(providerId, model));
    const key = pairKey(providerId, model);
    const s = this.map.get(key);
    if (!s) return true; // never seen → healthy

    // If blocked but the timeout has expired, auto-recover
    if (s.state === 'blocked' && s.stateExpiresAt !== null && Date.now() >= s.stateExpiresAt) {
      const oldState = s.state;
      s.state = 'healthy';
      s.stateExpiresAt = null;
      s.consecutiveFailures = Math.max(0, s.consecutiveFailures - 1); // reduce but don't fully reset
      this.emitStatusChanged(providerId, model, oldState, 'healthy', 'cooldown_expired');
      return true;
    }

    // If degraded but the timeout has expired, auto-recover
    if (s.state === 'degraded' && s.stateExpiresAt !== null && Date.now() >= s.stateExpiresAt) {
      const oldState = s.state;
      s.state = 'healthy';
      s.stateExpiresAt = null;
      this.emitStatusChanged(providerId, model, oldState, 'healthy', 'degraded_timeout_expired');
      return true;
    }

    return s.state !== 'blocked';
  }

  /**
   * Check if a (providerId, model) pair is currently rate-limited.
   * This is a stronger signal than just `!isAvailable()` — it tells
   * callers that requests should be re-tried after a delay rather
   * than skipped permanently.
   */
  isRateLimited(providerId: string, model: string): boolean {
    ({ providerId, model } = statusIdentity(providerId, model));
    const key = pairKey(providerId, model);
    const s = this.map.get(key);
    if (!s) return false;
    // If the last error was a rate_limit and we're still in a non-healthy state
    return s.lastErrorKind === 'rate_limit' && s.state !== 'healthy';
  }

  /**
   * Get the full status for a (providerId, model) pair, or `undefined`
   * if no failures have been recorded.
   */
  getStatus(providerId: string, model: string): ProviderModelStatus | undefined {
    ({ providerId, model } = statusIdentity(providerId, model));
    const key = pairKey(providerId, model);
    const s = this.map.get(key);
    if (!s) return undefined;

    // Auto-recover if stale
    this.isAvailable(providerId, model); // side-effect: recovers if expired

    return this.freezeStatus(key, s);
  }

  /**
   * Returns a snapshot of ALL tracked provider/model statuses.
   * Useful for the `/provider-status` command and WebUI.
   */
  getAllStatuses(): ProviderModelStatus[] {
    const out: ProviderModelStatus[] = [];
    for (const [key, s] of this.map) {
      // Trigger auto-recovery
      const [providerId, model] = unpairKey(key);
      this.isAvailable(providerId, model);
      out.push(this.freezeStatus(key, s));
    }
    return out;
  }

  /**
   * Move every expired waiting-room entry back to healthy. Runtime routing
   * already performs this check lazily; this explicit sweep lets a UI/status
   * timer refresh the room even while no model calls are being made.
   *
   * @returns Number of entries released by this sweep.
   */
  sweepExpired(): number {
    let released = 0;
    for (const [key, s] of this.map) {
      if (s.state === 'healthy' || s.stateExpiresAt === null || Date.now() < s.stateExpiresAt)
        continue;
      const [providerId, model] = unpairKey(key);
      const previous = s.state;
      s.state = 'healthy';
      s.stateExpiresAt = null;
      s.consecutiveFailures = 0;
      s.consecutiveSuccesses = 0;
      this.emitStatusChanged(providerId, model, previous, 'healthy', 'waiting_room_expired');
      released += 1;
    }
    return released;
  }

  /**
   * Release one entry for an immediate half-open probe on its next real use.
   * History and totals are retained so operators do not lose diagnostics.
   */
  retryNow(providerId: string, model: string): boolean {
    ({ providerId, model } = statusIdentity(providerId, model));
    const s = this.map.get(pairKey(providerId, model));
    if (!s || s.state === 'healthy') return false;
    const previous = s.state;
    s.state = 'healthy';
    s.stateExpiresAt = null;
    s.consecutiveFailures = 0;
    s.consecutiveSuccesses = 0;
    this.emitStatusChanged(providerId, model, previous, 'healthy', 'manual_half_open');
    return true;
  }

  /**
   * Returns only currently blocked entries.
   */
  getBlocked(): ProviderModelStatus[] {
    return this.getAllStatuses().filter((s) => s.state === 'blocked');
  }

  /**
   * Returns only currently degraded entries.
   */
  getDegraded(): ProviderModelStatus[] {
    return this.getAllStatuses().filter((s) => s.state === 'degraded');
  }

  /**
   * Filter an array of objects that have `providerId` and `model` fields,
   * removing entries whose (providerId, model) is blocked.
   */
  filterAvailable<T extends { providerId: string; model: string }>(entries: readonly T[]): T[] {
    return entries.filter((e) => this.isAvailable(e.providerId, e.model));
  }

  /**
   * Check if a (providerId, model) pair would be blocked, WITHOUT the
   * side-effect of auto-recovering stale entries. Used in hot paths
   * where we don't want to mutate state during a quick peek.
   */
  isBlocked(providerId: string, model: string): boolean {
    ({ providerId, model } = statusIdentity(providerId, model));
    const key = pairKey(providerId, model);
    const s = this.map.get(key);
    if (!s) return false;
    if (s.state !== 'blocked') return false;
    // Auto-recover even in this read-only-ish peek
    if (s.stateExpiresAt !== null && Date.now() >= s.stateExpiresAt) {
      return false; // would have recovered
    }
    return true;
  }

  /**
   * Reset tracking for a specific (providerId, model) pair, or for ALL
   * pairs when both arguments are omitted.
   */
  clear(providerId?: string, model?: string): void {
    if (providerId && model) {
      ({ providerId, model } = statusIdentity(providerId, model));
      const key = pairKey(providerId, model);
      const old = this.map.get(key);
      if (old && old.state !== 'healthy') {
        this.emitStatusChanged(providerId, model, old.state, 'healthy', 'manual_clear');
      }
      this.map.delete(key);
    } else {
      for (const [key, s] of this.map) {
        if (s.state !== 'healthy') {
          const [pid, mdl] = unpairKey(key);
          this.emitStatusChanged(pid, mdl, s.state, 'healthy', 'manual_clear_all');
        }
      }
      this.map.clear();
    }
  }

  /**
   * Get a JSON-safe snapshot suitable for WebUI rendering.
   * Includes summary stats + per-pair details.
   */
  getSnapshot(): ProviderStatusSnapshot {
    const all = this.getAllStatuses();
    const healthy: ProviderModelStatus[] = [];
    const degraded: ProviderModelStatus[] = [];
    const blocked: ProviderModelStatus[] = [];

    for (const s of all) {
      if (s.state === 'blocked') blocked.push(s);
      else if (s.state === 'degraded') degraded.push(s);
      else healthy.push(s);
    }

    return {
      totalPairs: all.length,
      healthy: healthy.length,
      degraded: degraded.length,
      blocked: blocked.length,
      totalFailures: all.reduce((sum, s) => sum + s.totalFailures, 0),
      totalRateLimits: all.reduce((sum, s) => sum + s.rateLimitHits, 0),
      statuses: all,
    };
  }

  /** Restore non-expired waiting-room entries from a previous process. */
  restoreSnapshot(value: unknown): number {
    if (!value || typeof value !== 'object') return 0;
    const statuses = (value as { statuses?: unknown }).statuses;
    if (!Array.isArray(statuses)) return 0;
    let restored = 0;
    const now = Date.now();
    for (const raw of statuses) {
      if (!raw || typeof raw !== 'object') continue;
      const item = raw as Record<string, unknown>;
      if (typeof item['providerId'] !== 'string' || typeof item['model'] !== 'string') continue;
      if (item['state'] !== 'blocked' && item['state'] !== 'degraded') continue;
      if (typeof item['stateExpiresAt'] !== 'number' || item['stateExpiresAt'] <= now) continue;
      const { providerId, model } = statusIdentity(item['providerId'], item['model']);
      const s = this.getOrCreate(pairKey(providerId, model), providerId, model);
      s.state = item['state'];
      s.stateExpiresAt = item['stateExpiresAt'];
      s.consecutiveFailures = safeCount(item['consecutiveFailures']);
      s.totalFailures = safeCount(item['totalFailures']);
      s.rateLimitHits = safeCount(item['rateLimitHits']);
      s.lastFailureAt = safeTimestamp(item['lastFailureAt']);
      s.lastErrorStatus = safeNullableNumber(item['lastErrorStatus']);
      s.lastErrorMessage =
        typeof item['lastErrorMessage'] === 'string' ? item['lastErrorMessage'] : null;
      s.lastErrorKind = isProviderErrorKind(item['lastErrorKind']) ? item['lastErrorKind'] : null;
      restored += 1;
    }
    return restored;
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  private getOrCreate(
    key: string,
    _providerId: string,
    _model: string,
  ): MutableProviderModelStatus {
    let s = this.map.get(key);
    if (!s) {
      s = {
        state: 'healthy',
        consecutiveFailures: 0,
        totalFailures: 0,
        rateLimitHits: 0,
        overloadedHits: 0,
        serverErrors: 0,
        otherErrors: 0,
        consecutiveSuccesses: 0,
        totalSuccesses: 0,
        lastSuccessAt: null,
        firstFailureAt: null,
        lastFailureAt: null,
        stateExpiresAt: null,
        lastErrorKind: null,
        lastErrorMessage: null,
        lastErrorStatus: null,
        lastSessionId: null,
        lastAgentId: null,
        recentErrors: [],
      };
      this.map.set(key, s);
    }
    return s;
  }

  private freezeStatus(key: string, s: MutableProviderModelStatus): ProviderModelStatus {
    const [providerId, model] = unpairKey(key);
    return Object.freeze({
      providerId,
      model,
      state: s.state,
      consecutiveFailures: s.consecutiveFailures,
      totalFailures: s.totalFailures,
      rateLimitHits: s.rateLimitHits,
      overloadedHits: s.overloadedHits,
      serverErrors: s.serverErrors,
      otherErrors: s.otherErrors,
      consecutiveSuccesses: s.consecutiveSuccesses,
      totalSuccesses: s.totalSuccesses,
      lastSuccessAt: s.lastSuccessAt,
      firstFailureAt: s.firstFailureAt,
      lastFailureAt: s.lastFailureAt,
      stateExpiresAt: s.stateExpiresAt,
      lastErrorKind: s.lastErrorKind,
      lastErrorMessage: s.lastErrorMessage,
      lastErrorStatus: s.lastErrorStatus,
      lastSessionId: s.lastSessionId,
      lastAgentId: s.lastAgentId,
      recentErrors: Object.freeze([...s.recentErrors]),
    });
  }

  private emitStatusChanged(
    providerId: string,
    model: string,
    oldState: ProviderModelState,
    newState: ProviderModelState,
    reason: string,
  ): void {
    if (!this.events) return;
    try {
      this.events.emit('provider.status_changed', {
        providerId,
        model,
        oldState,
        newState,
        reason,
        timestamp: Date.now(),
        stateExpiresAt: this.map.get(pairKey(providerId, model))?.stateExpiresAt ?? undefined,
      });
    } catch {
      // Swallow — event bus errors must not crash the tracker
    }
  }
}

// ── Snapshot type ───────────────────────────────────────────────────────────

export interface ProviderStatusSnapshot {
  readonly totalPairs: number;
  readonly healthy: number;
  readonly degraded: number;
  readonly blocked: number;
  readonly totalFailures: number;
  readonly totalRateLimits: number;
  readonly statuses: readonly ProviderModelStatus[];
}

// ── Key helpers ─────────────────────────────────────────────────────────────

const KEY_SEP = '\x00';

/**
 * OmniRoute is a transport gateway, not the logical provider identity. Its
 * discovered model ids are `<provider>/<model>`; strip the gateway prefix for
 * health tracking while callers continue using OmniRoute for the wire call.
 */
function statusIdentity(
  providerId: string,
  model: string,
): {
  providerId: string;
  model: string;
} {
  if (providerId !== 'omniroute') return { providerId, model };
  const slash = model.indexOf('/');
  if (slash <= 0 || slash === model.length - 1) return { providerId, model };
  return {
    providerId: model.slice(0, slash),
    model: model.slice(slash + 1),
  };
}

function pairKey(providerId: string, model: string): string {
  return `${providerId}${KEY_SEP}${model}`;
}

function unpairKey(key: string): [string, string] {
  const idx = key.indexOf(KEY_SEP);
  if (idx === -1) return [key, ''];
  return [key.slice(0, idx), key.slice(idx + 1)];
}

const QUOTA_EXHAUSTED_RE =
  /(?:insufficient|exhausted|depleted|exceeded|no|not enough)[-_\s]*(?:quota|credit|balance)|(?:quota|credit|balance)[-_\s]*(?:exhausted|depleted|exceeded|insufficient)|billing[_\s-]*(?:hard[_\s-]*)?limit|payment required|spending limit|plan limit|usage[-_\s]*limit[-_\s]*(?:reached|exceeded)|rate[-_\s]*limit[-_\s]*exceeded/i;

/** Distinguish an exhausted account/plan from an ordinary per-minute 429. */
function isQuotaExhausted(kind: ProviderErrorKind, status: number, message: string): boolean {
  if (status === 402) return true;
  if (
    kind !== 'rate_limit' &&
    kind !== 'quota_exhausted' &&
    kind !== 'invalid_request' &&
    kind !== 'auth'
  )
    return false;
  return QUOTA_EXHAUSTED_RE.test(message);
}

/**
 * Messages that indicate the provider's API endpoint is unreachable rather
 * than merely overloaded or transiently failing. Common in 502 Bad Gateway
 * responses when Cloudflare / AWS / Kong cannot reach the upstream service.
 *
 * Unlike a transient 503 or 504, an unreachable endpoint often signals a
 * longer-lasting outage (deployment roll, DNS propagation, upstream crash).
 * The tracker treats these like quota exhaustion — block on first hit
 * instead of burning through the failure-threshold chain.
 */
const ENDPOINT_UNREACHABLE_RE =
  /(?:upstream|origin|backend|endpoint)[-_\s]*(?:unreachable|refused|connect(?:ion)?[-_\s]*(?:refused|error|fail)|unavailable|down|timeout)|no[-_\s]*(?:healthy|valid)[-_\s]*upstream|cannot[-_\s]*connect|connection[-_\s]*(?:refused|reset|closed|timed?[-_\s]out)/i;

function isEndpointUnreachable(kind: ProviderErrorKind, status: number, message: string): boolean {
  // Only match gateway errors and network-level failures. Normal 5xx without
  // an unreachable message are transient server errors, not endpoint-down.
  if (status !== 502 && status !== 503 && status !== 0) return false;
  if (kind !== 'server' && kind !== 'network' && kind !== 'overloaded' && kind !== 'timeout')
    return false;
  return ENDPOINT_UNREACHABLE_RE.test(message);
}

function safeCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

function safeTimestamp(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function safeNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

const PROVIDER_ERROR_KINDS = new Set<ProviderErrorKind>([
  'rate_limit',
  'quota_exhausted',
  'overloaded',
  'server',
  'timeout',
  'network',
  'stream_hang',
  'auth',
  'context_overflow',
  'content_filter',
  'invalid_request',
  'unknown',
]);

function isProviderErrorKind(value: unknown): value is ProviderErrorKind {
  return typeof value === 'string' && PROVIDER_ERROR_KINDS.has(value as ProviderErrorKind);
}
